// @ts-nocheck
import {
  Container, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Stack, TextField, FormControl, InputLabel, Select, MenuItem, Typography, IconButton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import HistoryIcon from '@mui/icons-material/History';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';

import useAuth from '../../hooks/useAuth';
import useSettings from '../../hooks/useSettings';
import { arrangeDocIndexes, userRoles } from '../../constants/defaultValues';
import axios from '../../utils/axios';

import Page from '../../components/Page';
import PageHeading from '../../components/PageHeading';
import Logo from '../../components/Logo';
import DocumentSheet, { highlightDocTokens } from '../../components/documents/DocumentSheet';
import DocumentSkeleton from '../../components/documents/DocumentSkeleton';
import DocHistoryList from '../../components/documents/DocHistoryList';
import DocEditor from '../../components/editor/DocEditor';
import { exportDocumentToPdf } from '../../components/documents/documentPrint';
import Question from './conditions/Question';
import useWorkplaceTerm from '../../hooks/useWorkplaceTerm';

// ----------------------------------------------------------------------

const DOC_LABELS = {
  ptc: 'PTC',
  whs: 'WHS',
  wir: 'WIR',
  terms: 'Terms & Conditions',
  hazards: 'Hazardous Substances',
};
const DOC_SUBTITLES = {
  ptc: 'Period Trade Contract — the master contract new builders are seeded with.',
  whs: 'Work Health & Safety plan — filled with real job-site values when generated.',
  wir: 'Work Induction Record template.',
  terms: 'Platform terms shown to every role during onboarding.',
  hazards: 'Hazardous substances guidance.',
};

const sectionActions = ['Remove this section', 'Add this section'];

// IF condition options for per-section settings (job-site question).
const conditions = [
  {
    title: 'If this question is ticked while creating job site',
    name: 'jobsiteQuestion',
    component: (props) => <Question {...props} />,
  },
];

const jobsiteQuestionStyle = {
  display: 'flex',
  px: 2,
  py: 1,
  justifyContent: 'space-between',
  borderRadius: '7px',
};

function reorderList(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

export default function GeneralApp({ type }) {

  const workplace = useWorkplaceTerm();
  const { themeStretch } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [document, setDocument] = useState([]);
  const [docItems, setDocItems] = useState([]);
  const [editorModalOpen, toggleEditorModal] = useState(false);
  const [settingsModalOpen, toggleSettingsModal] = useState(false);
  const [sectionSettingModalOpen, toggleSectionSettingsModal] = useState(false);
  const [historyModalOpen, toggleHistoryModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [addSection, setAddSection] = useState(0);
  const [condition, setCondition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const docType = type;
  const toast = (variant, message) => enqueueSnackbar(message, { variant });

  useEffect(() => {
    if (user && user.id && user.role !== userRoles.admin && user.role !== userRoles.superadmin) navigate('/');
  }, []);

  const fetchAdminDoc = async (t) => {
    setFetching(true);
    try {
      const r = await axios.get(`/v1/documents/admin-docs?type=${t}`);
      const data = r.data?.data || r.data || [];
      setDocument(data);
      setDocItems(data);
    } catch (e) {
      console.error('fetchAdminDoc', e);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    setDocument([]);
    toggleEditorModal(false);
    if (type) fetchAdminDoc(type);
  }, [type]);

  // ---- section CRUD (refetch after each mutation; the API returns the
  // affected row, not the full list) -----------------------------------------

  const handleSaveDocumentSection = async () => {
    if (!content || !content.length) return;
    setLoading(true);
    try {
      if (currentSection?.id) {
        await axios.put(`/v1/documents/admin-docs/${currentSection.id}`, { content, title, docType });
      } else {
        await axios.post('/v1/documents/admin-docs', { content, docType, title });
      }
      await fetchAdminDoc(docType);
      toggleEditorModal(false);
      setContent('');
      setTitle('');
      setCurrentSection(null);
    } catch (e) {
      toast('warning', e.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPage = () => {
    setCurrentSection(null);
    setContent('');
    setTitle('');
    toggleEditorModal(true);
  };

  const handleEdit = (docSection) => {
    setCurrentSection({ ...docSection });
    setTitle(docSection.title || '');
    setContent(docSection.content || '');
    toggleEditorModal(true);
  };

  const handleDelete = async (docSection) => {
    try {
      await axios.delete(`/v1/documents/admin-docs/${docSection.id}?docType=${docType}`);
      await fetchAdminDoc(docType);
    } catch (e) {
      toast('warning', e.message || 'Failed to delete');
    }
  };

  // ---- reorder --------------------------------------------------------------

  const onDragEnd = (result) => {
    if (!result.destination) return;
    setDocItems(reorderList(docItems, result.source.index, result.destination.index));
  };

  const handleOrderUpdate = async () => {
    const sections = docItems.map((item, index) => ({ id: item.id, orderId: index }));
    setLoading(true);
    try {
      await axios.put('/v1/documents/admin-docs/order', { docType, sections });
      await fetchAdminDoc(docType);
      toggleSettingsModal(false);
    } catch (e) {
      toast('warning', e.message || 'Failed to reorder');
    } finally {
      setLoading(false);
    }
  };

  // ---- per-section settings (job-site question) -----------------------------

  const handleSectionSetting = (section) => {
    setCurrentSection(section);
    setCondition(null);
    toggleSectionSettingsModal(true);
  };

  const closeSectionSettingsModal = () => {
    toggleSectionSettingsModal(false);
    setCurrentSection(null);
    setCondition(null);
  };

  const handleChangeIF = (e) => {
    setCondition(conditions.filter((con) => con.name === e.target.value)[0]);
  };

  const handleUpdateSetting = async (data) => {
    setLoading(true);
    const fields = data.question
      ? { jobsiteQuestion: JSON.stringify({ ...data, addSection }) }
      : { ...data };
    try {
      await axios.put(`/v1/documents/admin-docs/${currentSection.id}/settings`, {
        docType: currentSection.docType,
        ...fields,
      });
      await fetchAdminDoc(docType);
      closeSectionSettingsModal();
    } catch (e) {
      toast('warning', e.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const getSavedConditions = () => {
    if (!currentSection) return null;
    const qData = currentSection.jobsiteQuestion ? JSON.parse(currentSection.jobsiteQuestion) : null;
    if (!qData) return null;
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>{workplace.noun} Question</Typography>
        <Box sx={{ ...jobsiteQuestionStyle, bgcolor: 'background.neutral' }}>
          <div>
            <Typography variant="body2">Question: {qData.question}</Typography>
            <Typography variant="body2">Action: {sectionActions[qData.addSection]}</Typography>
          </div>
          <IconButton onClick={() => handleUpdateSetting({ jobsiteQuestion: null })} aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  // ---- export ---------------------------------------------------------------

  const handleExportPdf = () => {
    try {
      const sections = arrangeDocIndexes(document).map((d) => ({ title: d.title, html: d.content }));
      exportDocumentToPdf(`WeSafe ${DOC_LABELS[docType] || ''} Document`, sections);
    } catch (e) {
      toast('warning', e.message || 'Could not export');
    }
  };

  const ordered = arrangeDocIndexes(document);

  return (
    <Page title={`WeSafe - ${DOC_LABELS[docType] || 'Documents'}`}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <PageHeading
          title={DOC_LABELS[docType] || 'Document'}
          subtitle={DOC_SUBTITLES[docType]}
          action={
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => toggleHistoryModal(true)}>
                History
              </Button>
              {ordered.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPdf}
                >
                  Export PDF
                </Button>
              )}
              {ordered.length > 1 && (
                <Button
                  variant="outlined"
                  startIcon={<TuneIcon />}
                  onClick={() => { setDocItems(document); toggleSettingsModal(true); }}
                >
                  Reorder
                </Button>
              )}
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewPage}>
                New section
              </Button>
            </Stack>
          }
        />

        {fetching ? (
          <DocumentSkeleton />
        ) : ordered.length === 0 ? (
          <Box
            sx={{
              border: (theme) => `1px dashed ${theme.palette.divider}`,
              borderRadius: 2,
              py: 8,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography sx={{ mb: 2 }}>No sections yet.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewPage}>
              Add the first section
            </Button>
          </Box>
        ) : (
          <Box sx={{ maxWidth: 860, mx: 'auto' }}>
            {/* One monolith document; each section highlights on hover and is
                click-to-edit, with settings/delete actions revealed on hover. */}
            <DocumentSheet
              header={<Logo showText sx={{ height: 26 }} />}
              footer="Copyright © WeSafe Pty Ltd · document generated at WeSafe.com.au"
            >
              {ordered.map((doc) => (
                <Box
                  key={doc.id}
                  onClick={() => handleEdit(doc)}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    borderRadius: 1,
                    border: '1px solid transparent',
                    transition: 'border-color .15s, background-color .15s',
                    px: 1,
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    '&:hover .doc-actions': { opacity: 1 },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.5}
                    className="doc-actions"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 3,
                      opacity: { xs: 1, md: 0 },
                      transition: 'opacity .2s',
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      boxShadow: 2,
                    }}
                  >
                    <IconButton size="small" aria-label="section settings" onClick={() => handleSectionSetting(doc)}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" aria-label="delete" onClick={() => handleDelete(doc)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <div
                    className="wesafe-doc safetyDoc"
                    dangerouslySetInnerHTML={{
                      __html: highlightDocTokens(
                        (doc.title ? `<h3>${doc.index}. ${doc.title}</h3>` : '') + (doc.content || ''),
                      ),
                    }}
                  />
                </Box>
              ))}
            </DocumentSheet>
          </Box>
        )}

        {/* ---- Editor modal ---- */}
        <Dialog fullWidth maxWidth="lg" open={editorModalOpen} onClose={() => toggleEditorModal(false)}>
          <DialogTitle>{currentSection ? 'Update section' : 'New section'}</DialogTitle>
          <DialogContent>
            {editorModalOpen && (
              <Box sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  sx={{ mb: 2 }}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  label="Section title"
                />
                <DocEditor docType={docType} content={content} onChange={setContent} />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => toggleEditorModal(false)}>Cancel</Button>
            <LoadingButton
              loading={loading}
              loadingPosition="start"
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSaveDocumentSection}
            >
              {currentSection ? 'Update' : 'Save'}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* ---- History modal ---- */}
        <Dialog fullWidth maxWidth="sm" open={historyModalOpen} onClose={() => toggleHistoryModal(false)}>
          <DialogTitle>{DOC_LABELS[docType] || 'Document'} — edit history</DialogTitle>
          <DialogContent>
            {historyModalOpen && <DocHistoryList url={`/v1/documents/admin-docs/history/${docType}`} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => toggleHistoryModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ---- Reorder modal ---- */}
        <Dialog fullWidth maxWidth="sm" open={settingsModalOpen} onClose={() => toggleSettingsModal(false)}>
          <DialogTitle>Reorder sections</DialogTitle>
          <DialogContent>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ py: 1 }}>
                    {docItems.map((item, index) => (
                      <Draggable key={String(item.id)} draggableId={String(item.id)} index={index}>
                        {(prov, snapshot) => (
                          <Stack
                            ref={prov.innerRef}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            sx={{
                              p: 1.5,
                              mb: 1,
                              borderRadius: 1.5,
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                              bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
                              ...prov.draggableProps.style,
                            }}
                          >
                            <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            <Typography variant="body2">
                              {item.title && item.title.length > 0 ? item.title : 'Untitled section'}
                            </Typography>
                          </Stack>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => toggleSettingsModal(false)}>Cancel</Button>
            <LoadingButton loading={loading} startIcon={<SaveIcon />} variant="contained" onClick={handleOrderUpdate}>
              Save order
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* ---- Section settings modal ---- */}
        <Dialog fullWidth maxWidth="md" open={sectionSettingModalOpen} onClose={closeSectionSettingsModal}>
          <DialogTitle>Section settings — {currentSection?.title || 'Untitled'}</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            {getSavedConditions()}
            {currentSection?.id && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel id="ifLabel">IF</InputLabel>
                  <Select
                    labelId="ifLabel"
                    label="IF"
                    value={condition?.name || ''}
                    onChange={handleChangeIF}
                  >
                    {conditions.map((con) => (
                      <MenuItem key={con.name} value={con.name}>{con.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {condition && (
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel id="thenLabel">THEN</InputLabel>
                    <Select
                      labelId="thenLabel"
                      label="THEN"
                      value={addSection}
                      onChange={(e) => setAddSection(e.target.value)}
                    >
                      <MenuItem value={0}>{sectionActions[0]}</MenuItem>
                      <MenuItem value={1}>{sectionActions[1]}</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Stack>
            )}
            {condition && (
              <Box sx={{ my: 2 }}>
                {condition.component({
                  loading,
                  condition,
                  section: currentSection,
                  toast,
                  handleUpdate: handleUpdateSetting,
                  handleCancel: closeSectionSettingsModal,
                })}
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Page>
  );
}
